<script setup lang="ts">
import { computed } from 'vue'

export type BoxSpace = '0' | '1' | '2' | '3' | '4' | '6'
export type BoxDirection = 'row' | 'col'
export type BoxAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline'
export type BoxJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly'

interface Props {
  /** Semantic HTML tag to render. Default `'div'`. */
  is?: string
  /** Apply `display: flex`. Also implicit when any of direction/align/justify/gap is set. */
  flex?: boolean
  /** Flex direction. Implies `display: flex`. */
  direction?: BoxDirection
  /** align-items value. Implies `display: flex`. */
  align?: BoxAlign
  /** justify-content value. Implies `display: flex`. */
  justify?: BoxJustify
  /** Flex gap. Token (rem scale) or any raw CSS length. */
  gap?: BoxSpace | (string & {})
  /** Padding (all 4 sides). Token or raw value. */
  padding?: BoxSpace | (string & {})
  /** Horizontal padding (left + right). Token or raw. */
  padX?: BoxSpace | (string & {})
  /** Vertical padding (top + bottom). Token or raw. */
  padY?: BoxSpace | (string & {})
  /** Width 100% (replaces `class="w-full"`). */
  block?: boolean
  /** `flex: 1` — grow & shrink to fill the parent's remaining space. */
  grow?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  is: 'div',
  flex: false,
  block: false,
  grow: false,
})

const SPACE_TOKENS = new Set<BoxSpace>(['0', '1', '2', '3', '4', '6'])
const isSpaceToken = (value?: string): value is BoxSpace =>
  !!value && SPACE_TOKENS.has(value as BoxSpace)

const isFlex = computed(
  () =>
    props.flex ||
    !!props.direction ||
    !!props.align ||
    !!props.justify ||
    props.gap !== undefined,
)

// Small helpers so `classes` reads as a flat list of class entries instead of
// a wall of ternaries. Each returns the class string or `null` (dropped by Vue).
const variantClass = (prefix: string, value?: string): string | null =>
  value ? `box--${prefix}-${value}` : null
const spaceClass = (prefix: string, value?: string): string | null =>
  isSpaceToken(value) ? `box--${prefix}-${value}` : null
const flagClass = (className: string, on: boolean): string | null => (on ? className : null)

const classes = computed(() => [
  flagClass('box--flex', isFlex.value),
  variantClass('direction', props.direction),
  variantClass('align', props.align),
  variantClass('justify', props.justify),
  spaceClass('gap', props.gap as string | undefined),
  spaceClass('padding', props.padding as string | undefined),
  spaceClass('padX', props.padX as string | undefined),
  spaceClass('padY', props.padY as string | undefined),
  flagClass('box--block', props.block),
  flagClass('box--grow', props.grow),
])

/**
 * Inline style only for raw escape-hatch values (e.g. `gap="0.5rem 1rem"`, `padding="20px"`).
 * Token values are handled by classes above; nothing goes to inline style for them.
 */
const inlineStyle = computed(() => {
  const style: Record<string, string> = {}
  const raw = (value?: string) => value !== undefined && !isSpaceToken(value)

  if (raw(props.gap as string | undefined)) style.gap = props.gap as string
  if (raw(props.padding as string | undefined)) style.padding = props.padding as string
  if (raw(props.padX as string | undefined)) {
    style.paddingLeft = props.padX as string
    style.paddingRight = props.padX as string
  }
  if (raw(props.padY as string | undefined)) {
    style.paddingTop = props.padY as string
    style.paddingBottom = props.padY as string
  }
  return Object.keys(style).length ? style : undefined
})
</script>

<template>
  <component :is="is" :class="classes" :style="inlineStyle">
    <slot />
  </component>
</template>

<style scoped>
.box--flex {
  display: flex;
}

/* Direction */
.box--direction-row {
  flex-direction: row;
}
.box--direction-col {
  flex-direction: column;
}

/* Align-items */
.box--align-start {
  align-items: flex-start;
}
.box--align-center {
  align-items: center;
}
.box--align-end {
  align-items: flex-end;
}
.box--align-stretch {
  align-items: stretch;
}
.box--align-baseline {
  align-items: baseline;
}

/* Justify-content */
.box--justify-start {
  justify-content: flex-start;
}
.box--justify-center {
  justify-content: center;
}
.box--justify-end {
  justify-content: flex-end;
}
.box--justify-between {
  justify-content: space-between;
}
.box--justify-around {
  justify-content: space-around;
}
.box--justify-evenly {
  justify-content: space-evenly;
}

/* Gap — rem scale */
.box--gap-0 {
  gap: 0;
}
.box--gap-1 {
  gap: 0.25rem;
}
.box--gap-2 {
  gap: 0.5rem;
}
.box--gap-3 {
  gap: 0.75rem;
}
.box--gap-4 {
  gap: 1rem;
}
.box--gap-6 {
  gap: 1.5rem;
}

/* Padding (all 4 sides) */
.box--padding-0 {
  padding: 0;
}
.box--padding-1 {
  padding: 0.25rem;
}
.box--padding-2 {
  padding: 0.5rem;
}
.box--padding-3 {
  padding: 0.75rem;
}
.box--padding-4 {
  padding: 1rem;
}
.box--padding-6 {
  padding: 1.5rem;
}

/* Horizontal padding */
.box--padX-0 {
  padding-left: 0;
  padding-right: 0;
}
.box--padX-1 {
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}
.box--padX-2 {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}
.box--padX-3 {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}
.box--padX-4 {
  padding-left: 1rem;
  padding-right: 1rem;
}
.box--padX-6 {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

/* Vertical padding */
.box--padY-0 {
  padding-top: 0;
  padding-bottom: 0;
}
.box--padY-1 {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}
.box--padY-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
.box--padY-3 {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}
.box--padY-4 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}
.box--padY-6 {
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
}

/* Sizing */
.box--block {
  width: 100%;
}
.box--grow {
  flex: 1;
}
</style>
